import { Badge, Card, Stack, Text } from "@mantine/core";
import { IconCheck, IconEdit, IconX } from "@tabler/icons-react";
import { CoverageQaProductCard } from "./CoverageQaProductCard";
import { HEADER_HEIGHT, RESULTS_SIDEBAR_WIDTH } from "@/config/data";
import type { CoverageQaResult, CoverageQaRejectionReason } from "@/types/coverage";

const REJECTION_REASON_LABELS: Record<CoverageQaRejectionReason, string> = {
  wrong_class: "Wrong Class",
  false_detection: "False Detection",
  image_quality: "Image Quality",
  obstructed_view: "Obstructed View",
  bad_angle: "Bad Angle",
  unsupported_planogram: "Unsupported Planogram",
};

const REJECTION_RESPONSE_LABELS: Record<string, string> = {
  incorrect_detection: "Incorrect Detection",
  incorrect_task: "Incorrect Task",
};

export const CoverageQaResultsSidebar = ({
  qaResult,
  qaTask,
  originalProductIds,
  productNameMap,
  productImageUrls,
}: {
  qaResult: CoverageQaResult;
  qaTask: { id?: string; completed_by?: string | null };
  originalProductIds: string[];
  productNameMap: Map<string, string>;
  productImageUrls: Map<string, string>;
}) => {
  const topOffset = HEADER_HEIGHT * 2;

  return (
    <div
      className="fixed right-0 bottom-0"
      style={{ top: topOffset - 2, width: RESULTS_SIDEBAR_WIDTH }}
    >
      <Card shadow="sm" padding="lg" withBorder className="h-full">
        <Stack gap="md">
          <Text size="lg" fw={600}>
            QA Result
          </Text>

          {/* Status Badge */}
          {qaResult.status === "approved" && (
            <Badge
              size="xl"
              color="green"
              leftSection={<IconCheck size={14} />}
            >
              Approved
            </Badge>
          )}

          {qaResult.status === "corrected" && (
            <>
              <Badge
                size="xl"
                color="yellow"
                leftSection={<IconEdit size={14} />}
              >
                Corrected
              </Badge>
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Corrected Products:
                </Text>
                <Stack gap="xs">
                  {qaResult.corrected_product_ids.map((id) => (
                    <CoverageQaProductCard
                      key={id}
                      productId={id}
                      name={productNameMap.get(id) ?? "Unknown product"}
                      imageUrl={productImageUrls.get(id)}
                    />
                  ))}
                </Stack>
              </div>
              {qaResult.corrected_planogram_id && (
                <div>
                  <Text size="sm" c="dimmed">
                    Corrected Planogram:
                  </Text>
                  <Text size="sm">{qaResult.corrected_planogram_id}</Text>
                </div>
              )}
            </>
          )}

          {qaResult.status === "rejected" && (
            <>
              <Badge size="xl" color="red" leftSection={<IconX size={14} />}>
                Rejected
              </Badge>
              {'response' in qaResult && qaResult.response && (
                <div>
                  <Text size="sm" c="dimmed">
                    Category:
                  </Text>
                  <Text size="sm" fw={500}>
                    {REJECTION_RESPONSE_LABELS[qaResult.response] ?? qaResult.response}
                  </Text>
                </div>
              )}
              <div>
                <Text size="sm" c="dimmed">
                  Reason:
                </Text>
                <Text size="sm" fw={500}>
                  {REJECTION_REASON_LABELS[qaResult.reason]}
                </Text>
              </div>
            </>
          )}

          {/* Original Products — shown for all statuses */}
          {originalProductIds.length > 0 && (
            <div>
              <Text size="sm" c="dimmed" mb="xs">
                Original Products:
              </Text>
              <Stack gap="xs">
                {originalProductIds.map((id) => (
                  <CoverageQaProductCard
                    key={id}
                    productId={id}
                    name={productNameMap.get(id) ?? "Unknown product"}
                    imageUrl={productImageUrls.get(id)}
                  />
                ))}
              </Stack>
            </div>
          )}

          {/* Metadata */}
          <div className="mt-auto pt-4 border-t border-gray-200">
            <Text size="xs" c="dimmed">
              Task ID: {qaTask.id?.slice(-8)}
            </Text>
            {qaTask.completed_by && (
              <Text size="xs" c="dimmed">
                Reviewed by: {qaTask.completed_by}
              </Text>
            )}
          </div>
        </Stack>
      </Card>
    </div>
  );
};
